import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Plus,
  Trash2,
  Download,
  Loader2,
  ExternalLink,
  Hammer,
  SlidersHorizontal,
  Building2,
  FileCheck2,
  Sparkles,
  Send,
  Save,
  RotateCcw,
  MessageSquareText,
  ListChecks,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Separator } from "./ui/separator";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { US_STATES } from "../lib/states";

import { Header } from "./Header";
import { Footer } from "./Footer";
import { UpgradeDialog } from "./UpgradeDialog";
import { QuotePreview } from "./QuotePreview";
import { TemplatePicker } from "./TemplatePicker";
import { CustomerPicker } from "./CustomerPicker";
import { useLocation } from "react-router-dom";
import { PriceChat } from "./PriceChat";
import { LeadsPanel } from "./leads/LeadsPanel";
import { PricingSection } from "./PricingSection";
import { SearchProgress } from "./SearchProgress";
import { useAuth } from "../context/AuthContext";
import { http, money, downloadBlob } from "../lib/api";

const FREE_KEY = "bidmark_free_search_used";
const todayStr = () =>
  new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const Field = ({ label, ...props }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">{label}</Label>
    <Input {...props} />
  </div>
);

export default function QuoteBuilder() {
  const { user, credits, refreshCredits } = useAuth();
  const isPaid = !!credits?.is_paid;

  const [form, setForm] = useState({ trade: "", job: "", city: "", state: "" });
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [customPrice, setCustomPrice] = useState("");
  const [selectedTier, setSelectedTier] = useState(null);

  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const [taxRate, setTaxRate] = useState("");

  const [company, setCompany] = useState({ name: "", phone: "", address: "", logo: "" });
  const [customer, setCustomer] = useState({ name: "", address: "", phone: "", email: "" });
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("form");
  const { hash } = useLocation();

  useEffect(() => {
    if (hash !== "#pricing") return;
    const t = setTimeout(() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }), 300);
    return () => clearTimeout(t);
  }, [hash]);
  const [template, setTemplate] = useState("basic");
  const allowedTemplates = credits?.templates || ["basic"];

  const [quoteNumber, setQuoteNumber] = useState(() => "Q-" + Math.floor(1000 + Math.random() * 9000));
  const [savedId, setSavedId] = useState(null);

  // First save creates the quote; later saves overwrite that same quote.
  const persistQuote = async () => {
    const payload = buildPayload(quoteNumber);
    const { data } = savedId ? await http.put(`/quotes/${savedId}`, payload) : await http.post("/quotes", payload);
    setSavedId(data.id);
    if (data.quote_number) setQuoteNumber(data.quote_number);
    return data;
  };

  const [chatKey, setChatKey] = useState(0);
  const startNewQuote = () => {
    if ((lines.length || customer.name || form.job) && !window.confirm("Clear this quote and start a new one? Saved quotes stay in Saved quotes.")) return;
    setForm({ trade: "", job: "", city: "", state: "" });
    setResult(null);
    setCustomPrice("");
    setSelectedTier(null);
    setLines([]);
    setNote("");
    setTaxRate("");
    setCustomer({ name: "", address: "", phone: "", email: "" });
    setSavedId(null);
    setQuoteNumber("Q-" + Math.floor(1000 + Math.random() * 9000));
    setChatKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("New quote started — your company details are kept.");
  };
  const date = useMemo(() => todayStr(), []);

  useEffect(() => {
    if (credits?.company) {
      setCompany((c) => ({
        name: credits.company.name || c.name,
        phone: credits.company.phone || c.phone,
        address: credits.company.address || c.address,
        logo: credits.company.logo || c.logo,
      }));
    }
  }, [credits]);

  const scrollToLines = () => {
    setTimeout(() => document.querySelector('[data-testid="line-editor"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };

  const applyPrefill = (p) => {
    setSavedId(null);
    setCustomer((c) => ({
      ...c,
      name: p.customer_name || c.name,
      phone: p.customer_phone || c.phone,
      email: p.customer_email || c.email,
    }));
    setForm((f) => ({ ...f, job: p.job_title || f.job, trade: f.trade || p.service || "" }));
    if (p.line && p.line.amount != null) {
      setLines([{ id: crypto.randomUUID(), description: p.line.description || p.job_title || "Job", amount: p.line.amount }]);
    }
    toast.message("Lead loaded — adjust the job, prices and any amounts before you send.");
    scrollToLines();
  };

  // Prefill from a lead picked on the Leads page (user can edit every amount before sending).
  useEffect(() => {
    const raw = localStorage.getItem("bidmark_lead_prefill");
    if (!raw) return;
    localStorage.removeItem("bidmark_lead_prefill");
    try {
      applyPrefill(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyEstimate = (est, tier) => {
    setSavedId(null);
    const src = est.sources?.[0] || null;
    setForm({ trade: est.trade || form.trade, job: est.job || form.job, city: est.city || form.city, state: est.state || form.state });
    setResult({ found: true, query: est.job, low: est.low, typical: est.typical, high: est.high, low_source: src, typical_source: src, high_source: src, sources: est.sources || [] });
    setSelectedTier(tier);
    setCustomPrice("");
    const breakdown = tier === "typical" && est.lines?.length ? est.lines : [{ description: est.job || "Job", amount: est[tier] }];
    setLines(breakdown.map((l) => ({ id: crypto.randomUUID(), description: l.description, amount: l.amount })));
    if (est.notes?.length && !note) setNote(est.notes[0]);
    toast.success("Sent to the Quote Builder — edit any line, add the customer, then download or send.");
    scrollToLines();
  };

  useEffect(() => {
    if (!allowedTemplates.includes(template)) setTemplate(allowedTemplates[0] || "basic");
  }, [credits]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateForm = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSearch = async () => {
    // Read what's actually in the boxes too, so phone autofill/dictation that skips onChange still counts.
    const dom = (k) => (document.querySelector(`[data-testid="${k}-input"]`)?.value || "").trim();
    const q = { ...form, trade: form.trade.trim() || dom("trade"), job: form.job.trim() || dom("job"), city: form.city.trim() || dom("city") };
    const missing = [["trade", "Trade"], ["city", "City"], ["job", "The job"]].filter(([k]) => !q[k]).map(([, l]) => l);
    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(", ")}.`);
      return;
    }
    setForm(q);
    // anonymous: one free search gated in the browser
    if (!user) {
      if (localStorage.getItem(FREE_KEY) === "1") {
        toast.message("Create an account to keep searching.");
        setUpgradeOpen(true);
        return;
      }
    }
    setSearching(true);
    setResult(null);
    setSelectedTier(null);
    try {
      const { data } = await http.post("/search-price", q);
      if (!user) localStorage.setItem(FREE_KEY, "1");
      setResult(data);
      if (user) refreshCredits();
      if (!data.found) toast.message("The search found nothing.");
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 402) {
        toast.message(detail === "pack_needed" ? "Out of searches — add a pack." : "Your free search is used — pick a plan.");
        setUpgradeOpen(true);
      } else {
        toast.error("Search failed. Please try again.");
      }
    } finally {
      setSearching(false);
    }
  };

  const setBaseLine = (value) => {
    const desc = form.job || "Job";
    setLines((prev) => {
      if (prev.length === 0) return [{ id: crypto.randomUUID(), description: desc, amount: value }];
      const copy = [...prev];
      copy[0] = { ...copy[0], description: copy[0].description || desc, amount: value };
      return copy;
    });
  };

  const pickTier = (tier, value) => {
    setSelectedTier(tier);
    setCustomPrice("");
    setBaseLine(value);
  };

  const applyCustom = () => {
    const val = parseFloat(customPrice);
    if (isNaN(val)) {
      toast.error("Type a number for the final price.");
      return;
    }
    setSelectedTier("custom");
    setBaseLine(val);
  };

  const addLine = () => setLines((p) => [...p, { id: crypto.randomUUID(), description: "", amount: "" }]);
  const startBlank = () => {
    if (lines.length === 0) setLines([{ id: crypto.randomUUID(), description: form.job || "", amount: "" }]);
    scrollToLines();
  };
  const updateLine = (id, k, v) => setLines((p) => p.map((l) => (l.id === id ? { ...l, [k]: v } : l)));
  const removeLine = (id) => setLines((p) => p.filter((l) => l.id !== id));

  const [pricingLineId, setPricingLineId] = useState(null);

  const autoPriceLine = async (line, { force = false } = {}) => {
    const desc = (line.description || "").trim();
    if (!desc) {
      if (force) toast.message("Type what the line is for first.");
      return;
    }
    if (!force && String(line.amount).trim() !== "") return; // don't overwrite a typed price
    if (!form.trade.trim() || !form.city.trim()) {
      if (force) toast.message("Fill the trade and city at the top first.");
      return;
    }
    setPricingLineId(line.id);
    try {
      const { data } = await http.post("/line-price", {
        trade: form.trade,
        city: form.city,
        state: form.state,
        description: desc,
      });
      if (data.found) {
        updateLine(line.id, "amount", data.typical);
      } else if (force) {
        toast.message("No typical price found for that line.");
      }
    } catch {
      if (force) toast.error("Could not fetch a price for that line.");
    } finally {
      setPricingLineId(null);
    }
  };

  const previewData = {
    company_name: company.name,
    company_phone: company.phone,
    company_address: company.address,
    logo: company.logo,
    quote_number: quoteNumber,
    date,
    customer_name: customer.name,
    customer_address: customer.address,
    customer_phone: customer.phone,
    customer_email: customer.email,
    job_title: form.job,
    lines: lines.map((l) => ({ description: l.description, amount: l.amount })),
    note,
    tax_rate: taxRate,
    template,
    is_free: !isPaid,
  };

  const buildPayload = (qn) => ({
    quote_number: qn,
    date,
    customer_name: customer.name || null,
    customer_address: customer.address || null,
    customer_phone: customer.phone || null,
    customer_email: customer.email || null,
    job_title: form.job || null,
    lines: lines.map((l) => ({ description: l.description, amount: parseFloat(l.amount) || 0 })),
    note: note || null,
    tax_rate: taxRate ? parseFloat(taxRate) : null,
    template,
  });

  const downloadPdf = async (payload, filename) => {
    const body = { ...payload, company_name: company.name || null, company_phone: company.phone || null, company_address: company.address || null };
    if (!user) body.logo = company.logo || null; // logged-in users get the logo from their account server-side
    const res = await http.post("/generate-pdf", body, { responseType: "blob" });
    downloadBlob(res.data, filename);
  };

  const handlePrimary = async () => {
    if (lines.length === 0) {
      toast.error("Build at least one line before making the quote.");
      return;
    }
    setDownloading(true);
    try {
      let qn = quoteNumber;
      if (isPaid) {
        try {
          const saved = await persistQuote();
          qn = saved.quote_number || quoteNumber;
          setQuoteNumber(qn);
        } catch (saveErr) {
          console.error("save failed, continuing to PDF", saveErr);
        }
        await downloadPdf(buildPayload(qn), `quote-${qn}.pdf`);
        toast.success("Quote saved and downloaded.");
      } else {
        await downloadPdf(buildPayload(quoteNumber), "quote-sample.pdf");
        toast.success("Sample quote downloaded.");
      }
    } catch (e) {
      const code = e.response?.status ? ` (${e.response.status})` : "";
      toast.error(`Could not build the PDF${code}. Please try again.`);
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveOnly = async () => {
    if (lines.length === 0) {
      toast.error("Build at least one line before saving the quote.");
      return;
    }
    setSending("saveonly");
    try {
      const saved = await persistQuote();
      setQuoteNumber(saved.quote_number || quoteNumber);
      toast.success("Quote saved — find it under Saved quotes.");
    } catch {
      toast.error("Could not save the quote. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const sendQuote = async (download) => {
    if (lines.length === 0) {
      toast.error("Build at least one line before sending the quote.");
      return;
    }
    if (!customer.email || !customer.email.trim()) {
      toast.error("Add the customer's email above before sending.");
      return;
    }
    setSending(download ? "download" : "save");
    try {
      const saved = await persistQuote();
      const qn = saved.quote_number || quoteNumber;
      setQuoteNumber(qn);
      if (download) await downloadPdf(buildPayload(qn), `quote-${qn}.pdf`);
      await http.post(`/quotes/${saved.id}/send`, { doc_type: "quote" });
      toast.success(download ? `Quote downloaded and emailed to ${customer.email}.` : `Quote saved and emailed to ${customer.email}.`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = e.response?.status ? ` (${e.response.status})` : "";
      toast.error(detail || `Could not send the quote${code}. Please try again.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header onUpgrade={() => setUpgradeOpen(true)} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="mb-10 grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3 max-w-2xl bg-white/75 backdrop-blur-md border border-[#E2DCD2] rounded-2xl p-6 sm:p-8 shadow-sm">
            <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#1C1915]">
              Write a quote you can stand behind.
            </h1>
            <p className="mt-3 text-base text-[#5C564E] leading-relaxed">
              Fill the form or just talk it through in the chat. BidMark reads real prices off the web and
              gives you a low, typical, and high to work from — each with its source. Find real leads right beside it.
            </p>
            {!user && (
              <a href="#pricing" data-testid="hero-see-pricing" className="mt-4 inline-flex items-center rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white px-5 h-10 text-sm font-medium transition-colors">
                See pricing — free to start, plans from $10/month
              </a>
            )}
          </div>
          <div className="hidden lg:block lg:col-span-2">
            <img
              src="/contractors.jpg"
              alt="Two contractors reviewing a quote together"
              className="w-full rounded-2xl shadow-lg border border-[#E2DCD2] object-cover aspect-square"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-8">
          <div className="lg:col-span-7">
            {/* search */}
            <section className="bg-white/80 backdrop-blur-md border border-[#E2DCD2] rounded-xl shadow-sm p-6 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-2">
                  <Hammer className="w-4 h-4 text-[#A6844A]" />
                  <h3 className="font-semibold text-[#1C1915]">Open a quote</h3>
                </div>
                <div className="inline-flex rounded-full bg-[#F1ECE3] p-1" data-testid="price-mode-toggle">
                  {[["form", "Form", ListChecks], ["chat", "Chat", MessageSquareText]].map(([k, label, Icon]) => (
                    <button key={k} data-testid={`price-mode-${k}`} onClick={() => setMode(k)} className={`px-4 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${mode === k ? "bg-[#1B3A4B] text-white shadow-sm" : "text-[#5C564E] hover:text-[#1B3A4B]"}`}>
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={mode === "chat" ? "" : "hidden"}>
                <PriceChat key={chatKey} user={user} isPaid={isPaid} onUse={applyEstimate} onNeedCoins={() => setUpgradeOpen(true)} onReplied={() => user && refreshCredits()} />
              </div>
              {mode === "form" && (
              <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Field label="Trade" data-testid="trade-input" placeholder="plumber" value={form.trade} onChange={(e) => updateForm("trade", e.target.value)} />
                <Field label="City" data-testid="city-input" placeholder="Austin" value={form.city} onChange={(e) => updateForm("city", e.target.value)} />
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">State</Label>
                  <Select value={form.state} onValueChange={(v) => updateForm("state", v)}>
                    <SelectTrigger data-testid="state-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {US_STATES.map((s) => (
                        <SelectItem key={s.code} value={s.code} data-testid={`state-option-${s.code}`}>
                          {s.name} ({s.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4 space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">The job, in plain words</Label>
                <Input data-testid="job-input" placeholder="40-gallon gas water heater, second floor" value={form.job} onChange={(e) => updateForm("job", e.target.value)} />
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Button data-testid="search-button" onClick={handleSearch} disabled={searching} className="rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white px-6 h-11">
                  {searching ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading prices…</>) : (<><Search className="w-4 h-4 mr-2" /> Find prices</>)}
                </Button>
                <Button data-testid="blank-quote-button" variant="outline" onClick={startBlank} disabled={searching} className="rounded-full border-[#A6844A] text-[#1B3A4B] px-6 h-11">
                  <Plus className="w-4 h-4 mr-2" /> Write quote without searching
                </Button>
              </div>
              <SearchProgress
                active={searching}
                rate={3}
                testid="quote-search-progress"
                steps={["Starting your price search…", "Searching local price pages…", "Cross-checking sources…", "Working out low, typical & high…", "Finishing up…"]}
                hint="Reading real prices off the web — usually 15–30 seconds."
              />
              </>
              )}
            </section>
          </div>
          <div className="lg:col-span-5">
            <LeadsPanel user={user} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7 space-y-6">

            {/* results */}
            {result && (
              <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white/80 backdrop-blur-md border border-[#E2DCD2] rounded-xl shadow-sm p-6 sm:p-8" data-testid="results-section">
                {result.found ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-[#1C1915]">Three numbers for this job</h3>
                      <span className="text-xs text-[#5C564E] truncate max-w-[50%]" title={result.query}>“{result.query}”</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {[["low", "Low", result.low, result.low_source], ["typical", "Typical", result.typical, result.typical_source], ["high", "High", result.high, result.high_source]].map(([tier, label, value, src]) => (
                        <button key={tier} data-testid={`price-option-${tier}`} onClick={() => pickTier(tier, value)} className={`text-left rounded-lg p-4 transition-colors duration-200 ${selectedTier === tier ? "bg-[#1B3A4B] text-white border-2 border-[#A6844A] shadow-md" : "bg-[#F4F0E8] text-[#1C1915] border border-[#D5CFC2] hover:bg-[#EAE4D9]"}`}>
                          <div className="text-xs font-semibold uppercase tracking-widest opacity-70">{label}</div>
                          <div className="font-serif text-2xl font-bold mt-1">{money(value)}</div>
                          {src?.url && (
                            <a href={src.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`price-source-${tier}`} className={`mt-2 inline-flex items-center gap-1 text-[11px] underline underline-offset-2 ${selectedTier === tier ? "text-white/80" : "text-[#5C564E]"}`}>
                              <ExternalLink className="w-3 h-3" /> {src.label?.slice(0, 26)}
                            </a>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="mt-5 flex flex-col sm:flex-row sm:items-end gap-3">
                      <div className="flex-1 space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">…or type a final price</Label>
                        <Input data-testid="custom-price-input" type="number" placeholder="1200" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)} />
                      </div>
                      <Button data-testid="apply-custom-price" variant="outline" onClick={applyCustom} className="rounded-full border-[#1B3A4B] text-[#1B3A4B] h-11">Use this price</Button>
                    </div>
                    {result.sources?.length > 0 && (
                      <div className="mt-5">
                        <div className="text-xs font-semibold uppercase tracking-widest text-[#5C564E] mb-2">Sources</div>
                        <div className="flex flex-wrap gap-2">
                          {result.sources.map((s, i) => (
                            <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F4F0E8] border border-[#D5CFC2] text-[#5C564E] hover:border-[#A6844A]">
                              <ExternalLink className="w-3 h-3" /> {s.label?.slice(0, 32)}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div data-testid="empty-result" className="text-center py-6">
                    <p className="font-serif text-xl text-[#1B3A4B]">The search found nothing.</p>
                    <p className="text-sm text-[#5C564E] mt-2">Try different words for the job or the city. BidMark does not invent a price.</p>
                  </div>
                )}
              </motion.section>
            )}

            {/* line editor */}
            {lines.length > 0 && (
              <section data-testid="line-editor" className="bg-white/80 backdrop-blur-md border border-[#E2DCD2] rounded-xl shadow-sm p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-5">
                  <SlidersHorizontal className="w-4 h-4 text-[#A6844A]" />
                  <h3 className="font-semibold text-[#1C1915]">Adjust the job</h3>
                </div>
                <div className="space-y-3">
                  {lines.map((l) => (
                    <div key={l.id} className="flex items-center gap-2">
                      <Input data-testid="line-description" className="flex-1" placeholder="extra hour, disposal, permit…" value={l.description} onChange={(e) => updateLine(l.id, "description", e.target.value)} onBlur={() => autoPriceLine(l)} />
                      <Button data-testid="auto-price-line" variant="ghost" size="icon" title="Fill the typical charge for this line" onClick={() => autoPriceLine(l, { force: true })} disabled={pricingLineId === l.id} className="text-[#A6844A] hover:bg-[#A6844A]/10 shrink-0">
                        {pricingLineId === l.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      </Button>
                      <Input data-testid="line-amount" type="number" className="w-28 font-mono" placeholder="0.00" value={l.amount} onChange={(e) => updateLine(l.id, "amount", e.target.value)} />
                      <Button data-testid="delete-line" variant="ghost" size="icon" onClick={() => removeLine(l.id)} className="text-[#9E2A2B] hover:bg-[#9E2A2B]/10 shrink-0"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-[#5C564E]">Type what the line is for, then tab out — BidMark fills the typical charge automatically. Tap <Sparkles className="inline w-3 h-3 text-[#A6844A]" /> to refetch, and edit any amount by hand.</p>
                <Button data-testid="add-line-button" variant="outline" onClick={addLine} className="mt-4 rounded-full border-[#A6844A] text-[#1B3A4B]"><Plus className="w-4 h-4 mr-1" /> Add a line</Button>
                <Separator className="my-6 bg-[#E2DCD2]" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Tax rate % (optional)</Label>
                    <Input data-testid="tax-rate-input" type="number" placeholder="8.25" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
                  </div>
                </div>
                <div className="mt-4 space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Note for the customer (optional)</Label>
                  <Textarea data-testid="note-input" placeholder="Price includes standard installation." value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
              </section>
            )}

            {/* company & customer */}
            <section className="bg-white/80 backdrop-blur-md border border-[#E2DCD2] rounded-xl shadow-sm p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-5">
                <Building2 className="w-4 h-4 text-[#A6844A]" />
                <h3 className="font-semibold text-[#1C1915]">Company & customer</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Company name" data-testid="company-name-input" placeholder="Harbor Line Plumbing" value={company.name} onChange={(e) => setCompany((c) => ({ ...c, name: e.target.value }))} />
                <Field label="Company phone" data-testid="company-phone-input" placeholder="(512) 555-0100" value={company.phone} onChange={(e) => setCompany((c) => ({ ...c, phone: e.target.value }))} />
                <div className="sm:col-span-2">
                  <Field label="Company address (optional)" data-testid="company-address-input" placeholder="123 Market St, Austin, TX 78701" value={company.address} onChange={(e) => setCompany((c) => ({ ...c, address: e.target.value }))} />
                </div>
                {isPaid && (
                  <div className="sm:col-span-2 flex items-center justify-between gap-3 pt-2 border-t border-[#E2DCD2]">
                    <span className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Customer</span>
                    <CustomerPicker onPick={(c) => setCustomer({ name: c.name || "", address: c.address || "", phone: c.phone || "", email: c.email || "" })} />
                  </div>
                )}
                <Field label="Customer name" data-testid="customer-name-input" placeholder="Maria Alvarez" value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} />
                <Field label="Customer phone" data-testid="customer-phone-input" placeholder="(512) 555-0199" value={customer.phone} onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))} />
                <div className="sm:col-span-2">
                  <Field label="Customer email (needed to send)" data-testid="customer-email-input" type="email" placeholder="maria@example.com" value={customer.email} onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))} />
                </div>
                <div className="sm:col-span-2">
                  <Field label="Customer address" data-testid="customer-address-input" placeholder="418 Oak Street, Austin" value={customer.address} onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))} />
                </div>
              </div>
            </section>
          </div>

          {/* preview */}
          <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-4">
            <div className="flex justify-end">
              <Button data-testid="start-new-quote-button" onClick={startNewQuote} className="rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-10 px-5">
                <RotateCcw className="w-4 h-4 mr-2" /> Start new quote
              </Button>
            </div>
            <QuotePreview data={previewData} />
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">Quote template</Label>
              <TemplatePicker value={template} onChange={setTemplate} allowed={allowedTemplates} onLocked={() => setUpgradeOpen(true)} />
              {allowedTemplates.length === 1 && (
                <p className="text-xs text-[#5C564E]">Unlock Modern, Premium & Signature designs with a paid plan.</p>
              )}
            </div>
            {isPaid && (
              <Button data-testid="save-quote-button" onClick={handleSaveOnly} disabled={downloading || !!sending} className="w-full rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-12 text-base">
                {sending === "saveonly" ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>) : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
              </Button>
            )}
            <div className={`grid gap-3 ${isPaid ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
              <Button data-testid="download-pdf-button" onClick={handlePrimary} disabled={downloading || !!sending} className="w-full rounded-full bg-[#A6844A] hover:bg-[#8f6f3b] text-white h-12 text-base">
                {downloading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building PDF…</>) : isPaid ? (<><FileCheck2 className="w-4 h-4 mr-2" /> Download and Save</>) : (<><Download className="w-4 h-4 mr-2" /> Download sample PDF</>)}
              </Button>
              {isPaid && (
                <Button data-testid="send-and-save-button" onClick={() => sendQuote(false)} disabled={downloading || !!sending} className="w-full rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-12 text-base">
                  {sending === "save" ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-2" /> Send and Save</>)}
                </Button>
              )}
            </div>
            {isPaid && (
              <Button data-testid="send-pdf-button" onClick={() => sendQuote(true)} disabled={downloading || !!sending} className="w-full rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-12 text-base">
                {sending === "download" ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-2" /> Send and Download</>)}
              </Button>
            )}
            {!isPaid && (
              <p className="text-xs text-center text-[#5C564E]">
                Free quotes download once with a <b>SAMPLE</b> watermark.{" "}
                <button data-testid="inline-upgrade" onClick={() => setUpgradeOpen(true)} className="text-[#1B3A4B] underline underline-offset-2 font-medium">
                  Go paid
                </button>{" "}
                to remove it and save your quotes.
              </p>
            )}
          </div>
        </div>
        {!user && <div className="mt-12"><PricingSection /></div>}
      </main>

      <UpgradeDialog open={upgradeOpen} onOpenChange={(v) => { setUpgradeOpen(v); if (!v && user) refreshCredits(); }} />
      <Footer />
    </div>
  );
}
